import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Download, Loader2 } from "lucide-react";

import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table";
import { useToast } from "src/components/ui/toast";
import {
  fetchAttendanceReport,
  attendanceReportExportUrl,
} from "src/features/attendance/api/attendanceApi";

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function presetRange(preset) {
  const now = new Date();
  if (preset === "month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: isoDate(from), to: isoDate(to) };
  }
  // week (Mon–Sun)
  const day = now.getDay() || 7;
  const from = new Date(now);
  from.setDate(now.getDate() - day + 1);
  const to = new Date(from);
  to.setDate(from.getDate() + 6);
  return { from: isoDate(from), to: isoDate(to) };
}

export default function AttendanceReportsPage() {
  const { toast } = useToast();
  const [preset, setPreset] = useState("week");
  const [range, setRange] = useState(presetRange("week"));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  function applyPreset(p) {
    setPreset(p);
    setRange(presetRange(p));
  }

  async function load() {
    setLoading(true);
    try {
      const data = await fetchAttendanceReport(range);
      setReport(data);
    } catch (error) {
      const status = error.response?.status;
      toast({
        variant: "destructive",
        title: "Error",
        description:
          status === 403
            ? "You don't have permission to view reports."
            : "Couldn't load the report.",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  const chartData = useMemo(
    () =>
      (report?.series || []).map((s) => ({
        date: s.date.slice(5),
        present: s.present_count,
      })),
    [report]
  );

  return (
    <div className="mx-auto max-w-[1120px]">
      <div className="mb-5 flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-slate-200 p-0.5">
            {["week", "month"].map((p) => (
              <Button
                key={p}
                variant="ghost"
                onClick={() => applyPreset(p)}
                className={`h-auto rounded px-3 py-1 text-[13px] capitalize ${preset === p ? "bg-[#ecebff] font-medium text-[#4f39f6] hover:bg-[#ecebff]" : "text-slate-600"
                  }`}
              >
                This {p}
              </Button>
            ))}
          </div>
          <a href={attendanceReportExportUrl(range)} download>
            <Button variant="outline">
              <Download className="mr-1.5 h-4 w-4" />
              Export CSV
            </Button>
          </a>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2 text-[13px] text-slate-500">
        <Input
          type="date"
          value={range.from}
          onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          className="h-8 w-auto"
        />
        <span>to</span>
        <Input
          type="date"
          value={range.to}
          onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          className="h-8 w-auto"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="mb-5 grid grid-cols-3 gap-3">
            <SummaryCard label="People present" value={report?.totals?.user_count ?? 0} />
            <SummaryCard label="Present-days" value={report?.totals?.present_days ?? 0} />
            <SummaryCard label="Working days" value={report?.totals?.working_days ?? 0} />
          </div>

          {/* Daily chart */}
          <div className="mb-6 rounded-lg border border-slate-200 p-4">
            <div className="mb-3 text-[13px] font-medium text-slate-700">Daily present count</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="present" fill="#4f39f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Per-user table */}
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <Table className="text-[13px]">
              <TableHeader className="bg-slate-50 text-left text-slate-500">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-9 font-medium text-slate-500">User</TableHead>
                  <TableHead className="h-9 font-medium text-slate-500">Present</TableHead>
                  <TableHead className="h-9 font-medium text-slate-500">Absent</TableHead>
                  <TableHead className="h-9 font-medium text-slate-500">Late</TableHead>
                  <TableHead className="h-9 font-medium text-slate-500">Avg in</TableHead>
                  <TableHead className="h-9 font-medium text-slate-500">Avg out</TableHead>
                  <TableHead className="h-9 font-medium text-slate-500">Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.summary || []).length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={7} className="py-6 text-center text-slate-400">
                      No attendance in this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  report.summary.map((row) => (
                    <TableRow key={row.user.id}>
                      <TableCell className="py-2.5 text-slate-900">{row.user.name}</TableCell>
                      <TableCell className="py-2.5">{row.present_days}</TableCell>
                      <TableCell className="py-2.5">{row.absent_days}</TableCell>
                      <TableCell className="py-2.5">{row.late_days}</TableCell>
                      <TableCell className="py-2.5">{row.avg_check_in || "—"}</TableCell>
                      <TableCell className="py-2.5">{row.avg_check_out || "—"}</TableCell>
                      <TableCell className="py-2.5">{row.attendance_rate}%</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="text-[12px] text-slate-500">{label}</div>
      <div className="mt-1 text-[24px] font-semibold text-slate-900">{value}</div>
    </div>
  );
}
